import { getGraphToken } from './graphAuth';
import type {
  SharePointDrive,
  SharePointFolder,
  SharePointLocation,
  SharePointSite,
  SharePointUploadedItem
} from './types';

const graphBase = 'https://graph.microsoft.com/v1.0';

/** Graph rejects a single PUT above 4 MiB; beyond that an upload session is required. */
const simpleUploadLimit = 4 * 1024 * 1024;

/** Must be a multiple of 320 KiB (Graph requirement). 32 x 320 KiB = 10 MiB. */
const chunkSize = 32 * 320 * 1024;

const maxAttempts = 4;
const maxPages = 10;

export class GraphError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code: string | null
  ) {
    super(message);
    this.name = 'GraphError';
  }
}

interface GraphPage<T> {
  value?: T[];
  '@odata.nextLink'?: string;
}

interface GraphErrorBody {
  error?: { code?: string; message?: string };
}

function retryDelayMs(response: Response, attempt: number): number {
  const header = Number(response.headers.get('Retry-After'));
  return Number.isFinite(header) && header > 0 ? header * 1000 : 500 * 2 ** attempt;
}

function isTransient(status: number): boolean {
  return status === 429 || status === 503 || status === 504;
}

async function toGraphError(response: Response): Promise<GraphError> {
  const body = (await response.json().catch(() => null)) as GraphErrorBody | null;
  return new GraphError(
    body?.error?.message ?? `Microsoft Graph returned ${response.status}.`,
    response.status,
    body?.error?.code ?? null
  );
}

/** Retries throttling (429) and brief outages, honouring Retry-After. */
async function withRetry(send: () => Promise<Response>): Promise<Response> {
  for (let attempt = 0; ; attempt++) {
    const response = await send();
    if (response.ok || !isTransient(response.status) || attempt === maxAttempts - 1) return response;
    await new Promise((resolve) => setTimeout(resolve, retryDelayMs(response, attempt)));
  }
}

async function graphFetch(url: string, init: RequestInit = {}): Promise<Response> {
  const response = await withRetry(async () =>
    fetch(url, {
      ...init,
      headers: { Authorization: `Bearer ${await getGraphToken()}`, ...init.headers }
    })
  );

  if (!response.ok) throw await toGraphError(response);
  return response;
}

async function graphJson<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await graphFetch(path.startsWith('http') ? path : `${graphBase}${path}`, init);
  return (await response.json()) as T;
}

async function collectPages<T>(firstPath: string): Promise<T[]> {
  const items: T[] = [];
  let next: string | undefined = firstPath;

  for (let page = 0; next && page < maxPages; page++) {
    const body: GraphPage<T> = await graphJson<GraphPage<T>>(next);
    items.push(...(body.value ?? []));
    next = body['@odata.nextLink'];
  }

  return items;
}

export async function searchSites(query: string): Promise<SharePointSite[]> {
  const term = query.trim() || '*';
  const sites = await collectPages<{ id: string; displayName?: string; name?: string; webUrl: string }>(
    `/sites?search=${encodeURIComponent(term)}&$select=id,displayName,name,webUrl&$top=50`
  );

  return sites
    .map((site) => ({ id: site.id, name: site.displayName ?? site.name ?? site.webUrl, webUrl: site.webUrl }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export async function listDrives(siteId: string): Promise<SharePointDrive[]> {
  const drives = await collectPages<{ id: string; name: string }>(
    `/sites/${encodeURIComponent(siteId)}/drives?$select=id,name`
  );
  return drives.map(({ id, name }) => ({ id, name }));
}

/** Folders only: `$filter` on children is unreliable, so the file items are dropped here. */
export async function listFolders(driveId: string, folderId: string | null): Promise<SharePointFolder[]> {
  const parent = folderId ? `items/${encodeURIComponent(folderId)}` : 'root';
  const children = await collectPages<{ id: string; name: string; folder?: object }>(
    `/drives/${encodeURIComponent(driveId)}/${parent}/children?$select=id,name,folder&$top=200`
  );

  return children
    .filter((child) => child.folder)
    .map(({ id, name }) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export async function createFolder(driveId: string, parentId: string | null, name: string): Promise<SharePointFolder> {
  const parent = parentId ? `items/${encodeURIComponent(parentId)}` : 'root';
  const created = await graphJson<{ id: string; name: string }>(`/drives/${encodeURIComponent(driveId)}/${parent}/children`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, folder: {}, '@microsoft.graph.conflictBehavior': 'fail' })
  });
  return { id: created.id, name: created.name };
}

export function locationFolderId(location: SharePointLocation): string | null {
  return location.folders.at(-1)?.id ?? null;
}

/** False for any failure, since an unreachable or deleted folder is all the picker needs to know. */
export async function canAccessLocation(location: SharePointLocation): Promise<boolean> {
  const folderId = locationFolderId(location);
  const parent = folderId ? `items/${encodeURIComponent(folderId)}` : 'root';

  try {
    await graphJson(`/drives/${encodeURIComponent(location.driveId)}/${parent}?$select=id`);
    return true;
  } catch {
    return false;
  }
}

function itemPath(location: SharePointLocation, fileName: string): string {
  const folderId = locationFolderId(location);
  const parent = folderId ? `items/${encodeURIComponent(folderId)}` : 'root';
  return `/drives/${encodeURIComponent(location.driveId)}/${parent}:/${encodeURIComponent(sanitiseFileName(fileName))}:`;
}

/** Lets the user choose between replacing and renaming instead of overwriting silently. */
export async function fileExists(location: SharePointLocation, fileName: string): Promise<boolean> {
  try {
    await graphFetch(`${graphBase}${itemPath(location, fileName)}?$select=id`);
    return true;
  } catch (error) {
    if (error instanceof GraphError && error.status === 404) return false;
    throw error;
  }
}

/** OneDrive has no site, so a location in it carries this marker in `siteId` (matches the API). */
export const ONEDRIVE_SITE_ID = 'onedrive';

export function isOneDrive(location: Pick<SharePointLocation, 'siteId'>): boolean {
  return location.siteId === ONEDRIVE_SITE_ID;
}

/** The signed-in user's own OneDrive; a 404 means the account has none. */
export async function getMyDrive(): Promise<SharePointDrive> {
  const drive = await graphJson<{ id: string; name: string }>('/me/drive?$select=id,name');
  return { id: drive.id, name: drive.name };
}

/**
 * The root of a Microsoft 365 group's team site library. Graph answers 403/404 when the
 * signed-in user cannot reach it or the group has no site, which callers treat as "no location".
 */
export async function getGroupLocation(groupId: string, groupName: string): Promise<SharePointLocation> {
  const [site, drive] = await Promise.all([
    graphJson<{ id: string; displayName?: string }>(`/groups/${encodeURIComponent(groupId)}/sites/root?$select=id,displayName`),
    graphJson<{ id: string; name: string }>(`/groups/${encodeURIComponent(groupId)}/drive?$select=id,name`)
  ]);

  return {
    siteId: site.id,
    siteName: site.displayName ?? groupName,
    driveId: drive.id,
    driveName: drive.name,
    folders: []
  };
}

export function locationPath(location: SharePointLocation): string {
  return ['', ...location.folders.map((folder) => folder.name)].join('/') || '/';
}

/** Where a location is, for people: OneDrive has no separate library name to show. */
export function describeLocation(location: SharePointLocation): string {
  const where = isOneDrive(location) ? location.siteName : `${location.siteName} / ${location.driveName}`;
  return `${where}${locationPath(location)}`;
}

/** SharePoint rejects these characters and trailing dots or spaces. */
export function sanitiseFileName(name: string): string {
  const cleaned = name.replace(/["*:<>?/\\|]/g, '_').trim().replace(/\.+$/, '');
  return cleaned || 'document';
}

/**
 * Overwriting an existing file adds a version, provided the library has versioning on
 * (the SharePoint default), so a repeat save never fails or forks a copy.
 */
export async function uploadFile(
  location: SharePointLocation,
  fileName: string,
  content: Blob
): Promise<SharePointUploadedItem> {
  const itemPathValue = itemPath(location, fileName);

  if (content.size <= simpleUploadLimit) {
    const item = await graphJson<{ id: string; webUrl: string }>(
      `${itemPathValue}/content?@microsoft.graph.conflictBehavior=replace`,
      { method: 'PUT', headers: { 'Content-Type': 'application/octet-stream' }, body: content }
    );
    return { id: item.id, webUrl: item.webUrl };
  }

  const session = await graphJson<{ uploadUrl: string }>(`${itemPathValue}/createUploadSession`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ item: { '@microsoft.graph.conflictBehavior': 'replace' } })
  });

  return uploadInChunks(session.uploadUrl, content);
}

/** The session URL is pre-authorised, so no bearer token goes with these requests. */
async function uploadInChunks(uploadUrl: string, content: Blob): Promise<SharePointUploadedItem> {
  for (let start = 0; start < content.size; start += chunkSize) {
    const end = Math.min(start + chunkSize, content.size);

    const response = await withRetry(() =>
      fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Range': `bytes ${start}-${end - 1}/${content.size}` },
        body: content.slice(start, end)
      })
    );

    if (!response.ok) throw await toGraphError(response);

    if (end === content.size) {
      const item = (await response.json()) as { id: string; webUrl: string };
      return { id: item.id, webUrl: item.webUrl };
    }
  }

  throw new GraphError('The upload ended before the last chunk was sent.', 0, null);
}
